/**
 * BrainFit içerik editörü yapılandırması.
 *
 * Editör daha önce hiçbir `init` almıyordu: TinyMCE varsayılanıyla açılıyor,
 * gövde Helvetica 14px koyu gri olarak görünüyordu. Yazılar Word'den
 * yapıştırıldığı için de kendi satır içi biçimlerini (Arial 14pt, #3c210e,
 * `line-height: 19.5pt`, `<p>&nbsp;</p>` boşlukları) taşıyordu. Sonuç: panelde
 * görünen şey yayınlanan sayfaya hiç benzemiyordu ve biçim vermek zorlaşıyordu.
 *
 * Buradaki `content_style`, sitedeki `.bf-article-prose` kurallarının aynısıdır
 * (bkz. ekaakademi/public/assets/css/bf-pages.css). Satır içi biçimler hem
 * yapıştırmada engellenir (`invalid_styles`) hem de görünümde nötrlenir; yayın
 * tarafında zaten `App\Utils\Prose` ile temizleniyor. Böylece editördeki
 * görüntü ile yayınlanan sayfa aynı şeyi gösterir.
 */

const FONTS_URL =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Plus+Jakarta+Sans:wght@600;700&display=swap';

const INK = '#16191D';
const INK_2 = '#454B52';
const INK_3 = '#6B7178';
const ACCENT = '#0076A8';
const ACCENT_BRIGHT = '#54B8E3';
const SURFACE_2 = '#F7F9FB';
const LINE = '#E6EAEE';

const FONT_BODY = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";
const FONT_HEADING = "'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, sans-serif";

export const editorContentStyle = `
@import url('${FONTS_URL}');

body {
  font-family: ${FONT_BODY};
  font-size: 16px;
  line-height: 1.75;
  color: ${INK_2};
  background: #FFFFFF;
  max-width: 720px;
  margin: 0 auto;
  padding: 28px 24px 64px;
}

/* Eski içerikteki satır içi Arial/pt/renk bildirimlerini editörde de nötrle —
   yayında Prose::clean bunları zaten atıyor, editör de aynısını göstersin. */
body p, body span, body div, body li, body td, body th, body font {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
  background: none !important;
  letter-spacing: normal !important;
}

p { margin: 0 0 1.15em; }

h1, h2, h3, h4, h5 {
  font-family: ${FONT_HEADING} !important;
  color: ${INK} !important;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

h2 { font-size: 22px !important; font-weight: 700; margin: 2em 0 0.5em; }
h3 { font-size: 18px !important; font-weight: 700; margin: 1.75em 0 0.45em; }
h4, h5 { font-size: 16px !important; font-weight: 700; margin: 1.5em 0 0.4em; }

strong, b { color: ${INK} !important; font-weight: 700; }

/* Tek başına kalın paragraf = editörde başlık niyetine yazılmış satır.
   Yayında da böyle görünüyor. */
p > strong:only-child {
  display: block;
  font-family: ${FONT_HEADING} !important;
  font-size: 18px !important;
  line-height: 1.25;
}

a { color: ${ACCENT} !important; text-decoration: underline; text-underline-offset: 0.18em; }

ul, ol { margin: 0 0 1.15em; padding-left: 1.25em; }
li { margin-bottom: 0.4em; }

ul { list-style: none; padding-left: 0; }
ul > li { position: relative; padding-left: 1.5em; }
ul > li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.62em;
  width: 0.5em;
  height: 0.5em;
  border-radius: 40% 60% 55% 45% / 55% 45% 60% 40%;
  background: ${ACCENT_BRIGHT};
}
ul ul { margin-top: 0.4em; padding-left: 1.25em; }

blockquote {
  margin: 1.75em 0;
  padding: 20px 24px;
  background: ${SURFACE_2};
  border-left: 3px solid ${ACCENT_BRIGHT};
  border-radius: 10px;
  font-size: 17px !important;
  color: ${INK} !important;
}
blockquote p:last-child { margin-bottom: 0; }

img { max-width: 100%; height: auto; border-radius: 14px; margin: 1.75em auto; display: block; }

table { border-collapse: collapse; margin: 1.75em 0; font-size: 14px !important; }
th, td { padding: 12px 16px; border: 1px solid ${LINE}; text-align: left; }
th { background: ${SURFACE_2}; color: ${INK} !important; font-weight: 600; }

hr { margin: 2em 0; border: 0; border-top: 1px solid ${LINE}; }

/* Word'den gelen boş paragraflar editörde de görünür olsun ki silinebilsinler */
p:empty::after { content: "⌫ boş satır"; color: ${INK_3}; font-size: 12px; opacity: 0.5; }
`;

/** Satır içi biçim taşıyan düğümleri temizler — `Prose::clean` ile aynı kurallar */
function cleanNode(root: HTMLElement): void {
    root.querySelectorAll('font').forEach((node) => {
        const parent = node.parentNode as Node;
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
    });

    root.querySelectorAll<HTMLElement>('*').forEach((node) => {
        ['align', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'face', 'valign'].forEach((attr) => {
            node.removeAttribute(attr);
        });

        const align = node.style ? node.style.textAlign : '';
        node.removeAttribute('style');
        if (align && ['P', 'DIV', 'TD', 'TH', 'LI', 'H2', 'H3', 'H4'].indexOf(node.tagName) > -1) {
            node.style.textAlign = align;
        }

        if (node.tagName === 'SPAN' && node.attributes.length === 0) {
            const parent = node.parentNode as Node;
            while (node.firstChild) parent.insertBefore(node.firstChild, node);
            parent.removeChild(node);
        }
    });

    // Yalnızca boşluk taşıyan bloklar
    Array.from(root.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6'))
        .reverse()
        .forEach((node) => {
            if (node.querySelector('img, iframe, table, hr, video')) return;
            if ((node.textContent || '').replace(/ |​/g, ' ').trim() === '') {
                node.parentNode && node.parentNode.removeChild(node);
            }
        });

    // "·  madde" paragrafları → gerçek liste
    const marker = /^\s*([·•◦▪‣]|[-*])\s+/;
    let group: HTMLElement[] = [];
    const flush = () => {
        if (group.length > 1) {
            const list = root.ownerDocument!.createElement('ul');
            group[0].parentNode!.insertBefore(list, group[0]);
            group.forEach((paragraph) => {
                const item = root.ownerDocument!.createElement('li');
                while (paragraph.firstChild) item.appendChild(paragraph.firstChild);
                const first = item.firstChild;
                if (first && first.nodeType === 3) {
                    first.nodeValue = (first.nodeValue || '').replace(/ /g, ' ').replace(marker, '');
                }
                list.appendChild(item);
                paragraph.parentNode!.removeChild(paragraph);
            });
        }
        group = [];
    };

    Array.from(root.querySelectorAll('p')).forEach((paragraph) => {
        const text = (paragraph.textContent || '').replace(/ /g, ' ');
        const isItem = marker.test(text) && text.trim().length > 2;
        const continues = group.length > 0 && group[group.length - 1].parentNode === paragraph.parentNode;
        if (isItem && (group.length === 0 || continues)) {
            group.push(paragraph as HTMLElement);
            return;
        }
        flush();
        if (isItem) group.push(paragraph as HTMLElement);
    });
    flush();
}

export const editorInit: Record<string, any> = {
    height: 620,
    menubar: false,
    branding: false,
    convert_urls: false,
    plugins:
        'advlist autolink lists link image charmap searchreplace visualblocks code fullscreen table paste wordcount hr',
    toolbar: [
        'formatselect | bold italic underline | bullist numlist | outdent indent | blockquote hr',
        'alignleft aligncenter alignright | link image table | removeformat bfclean | searchreplace visualblocks code fullscreen',
    ],
    /* Başlık seçenekleri sitede karşılığı olanlarla sınırlı — panelde H1 seçilirse
       sayfada iki H1 olurdu (SEO). H1 sayfa başlığından geliyor. */
    block_formats: 'Paragraf=p; Başlık 2=h2; Başlık 3=h3; Ara başlık=h4; Alıntı=blockquote',
    content_style: editorContentStyle,
    /* Yapıştırırken biçim değil metin yapısı alınır: Word'ün font/renk/ölçü
       bildirimleri hiç girmez, `<ul>`, `<b>`, bağlantı gibi anlam korunur. */
    paste_as_text: false,
    paste_data_images: false,
    paste_merge_formats: true,
    paste_remove_styles_if_webkit: true,
    paste_webkit_styles: 'none',
    invalid_styles: {
        '*': 'font-family font-size font-weight line-height color background background-color letter-spacing margin margin-top margin-bottom padding border width height',
    },
    setup(editor: any) {
        editor.ui.registry.addButton('bfclean', {
            icon: 'clear-formatting',
            tooltip: 'Word biçimlerini temizle (tüm içerik)',
            onAction() {
                const doc = editor.getDoc();
                const holder = doc.createElement('div');
                holder.innerHTML = editor.getContent();
                cleanNode(holder);
                editor.setContent(holder.innerHTML);
                editor.notificationManager.open({
                    text: 'İçerik temizlendi: satır içi biçimler kaldırıldı, boş satırlar atıldı, maddeler listeye çevrildi.',
                    type: 'success',
                    timeout: 4000,
                });
            },
        });
    },
};

export default editorInit;
